import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useSiteConfig, pickI18n } from '../contexts/SiteConfigContext';
import { Calendar, Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
];

const Header = () => {
  const { lang, setLang } = useLang();
  const { config } = useSiteConfig();
  const colors = config.identity?.colors || {};
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = (config.nav || []).filter((n) => n.visible !== false);
  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  const bookLabel = lang === 'de' ? 'Termin buchen' : lang === 'ar' ? 'حجز موعد' : 'Book Appointment';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_16px_-8px_rgba(196,71,122,0.15)]'
          : 'bg-white/85 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            {config.identity?.logo && (
              <img
                src={config.identity.logo}
                alt={config.identity?.site_name || ''}
                className="w-12 h-12 object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
            )}
            <div className="hidden sm:block leading-tight">
              <div
                className="font-display text-base"
                style={{ color: colors.text_dark || '#5a1d3b', fontWeight: 500, letterSpacing: '0.01em' }}
              >
                {config.identity?.site_name?.split(' ').slice(0, 2).join(' ') || 'Dalaa Beauty'}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                {config.identity?.site_name?.split(' ').slice(2).join(' ') || ''}
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {navItems.map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className="relative text-[14px] transition-colors duration-200 py-1.5"
                  style={{
                    color: active ? (colors.primary || '#c4477a') : '#374151',
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {pickI18n(item.label, lang)}
                  <span
                    className="absolute -bottom-1 left-0 h-px transition-all duration-300"
                    style={{
                      width: active ? '100%' : '0%',
                      backgroundColor: colors.primary || '#c4477a',
                    }}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-medium uppercase">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`cursor-pointer ${lang === l.code ? 'bg-pink-50' : ''}`}
                    style={lang === l.code ? { color: colors.primary || '#c4477a' } : {}}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              onClick={() => navigate('/booking')}
              className="hidden md:inline-flex btn-rose text-white rounded-full px-5 h-11 gap-2 font-medium shadow-sm"
              style={{ backgroundColor: colors.primary || '#c4477a' }}
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} />
              {bookLabel}
            </Button>

            <button
              className="lg:hidden p-2 text-gray-700"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden py-4 border-t border-pink-100 bg-white/98 backdrop-blur-md">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-lg transition-colors"
                  style={
                    isActive(item.to)
                      ? { backgroundColor: '#fdf2f5', color: colors.primary || '#c4477a' }
                      : { color: '#374151' }
                  }
                >
                  {pickI18n(item.label, lang)}
                </Link>
              ))}
              <Button
                onClick={() => { setMobileOpen(false); navigate('/booking'); }}
                className="mt-2 btn-rose text-white rounded-full gap-2"
                style={{ backgroundColor: colors.primary || '#c4477a' }}
              >
                <Calendar className="w-4 h-4" />
                {bookLabel}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
